import {
    ACard,
    ADataGrid,
    ADataGridColumn,
} from "@atiautomacao/ati-ui-library";
import React, {FunctionComponent, useEffect, useState} from "react";
import {
    Box,
    Divider,
    Stack,
    TextField, Tooltip,
    Typography,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import {InfoOutlined, SimCardDownloadOutlined} from "@mui/icons-material";
import UnavailabilityBarChart from "./UnavailabilityBarChart";
import {
    ExportDinDexLossesReactQuery,
    FindUnavailabilityPowerStationsReactQuery
} from "../reactQuery/UnavailabilityReactQuery";
import CloseIcon from "@mui/icons-material/Close";
import {DatePicker, LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, {Dayjs} from "dayjs";
import {useSnackbar} from "notistack";
import {formatNumberWithMaxTwoDecimals} from "../../../../Utils/NumberUtil";
import themeDefaults from "../../../../Shared/Theme/ThemeDefaults";
import {
    buildDateFilterValidationMessage,
    validateAndConvertDateFilters
} from "../../../../Utils/AlarmDateFilterValidationUtils";

export const columns = [
    {
        name: 'position',
        label: '#',
        align: 'left',
        width: 10,
        minWidth: 10,
        visibility: true,
        breakpoints: {
            xs: true,
            sm: true,
            md: true,
            lg: true,
            xl: true
        },
    },
    {
        name: 'station',
        label: 'Usina',
        align: 'left',
        minWidth: 10,
        visibility: true,
        breakpoints: {
            xs: true,
            sm: true,
            md: true,
            lg: true,
            xl: true
        }
    },
    {
        name: 'din',
        label: 'DIN(kWh)',
        align: 'left',
        minWidth: 100,
        visibility: true,
        breakpoints: {
            xs: true,
            sm: true,
            md: true,
            lg: true,
            xl: true
        },
    },
    {
        name: 'dex',
        label: 'DEX (kWh)',
        align: 'left',
        minWidth: 100,
        visibility: true,
        breakpoints: {
            xs: true,
            sm: true,
            md: true,
            lg: true,
            xl: true
        },
    },
    {
        name: 'total',
        label: 'Total (kWh)',
        align: 'left',
        minWidth: 100,
        visibility: true,
        breakpoints: {
            xs: true,
            sm: true,
            md: true,
            lg: true,
            xl: true
        },
    },
    {
        name: 'totalPerCent',
        label: '% do total',
        align: 'left',
        minWidth: 100,
        visibility: true,
        breakpoints: {
            xs: true,
            sm: true,
            md: true,
            lg: true,
            xl: true
        },
    }
] as ADataGridColumn[];

const style = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '80%',
    bgcolor: 'background.paper',
    //border: '1px solid #666',
     borderRadius: '25px'
}

interface UnavailabilityRow {
    position: number,
    station: string,
    din: number,
    dex: number,
    total: number,
    totalPerCent: number
}

const MAX_RANGE_DAYS = 60;

function validateDateRange(
    start: Dayjs | string | null,
    end: Dayjs | string | null,
    changedField: 'start' | 'end',
    maxRangeDays: number = MAX_RANGE_DAYS,
): string | null {
    const startDate = start ? dayjs(start) : null;
    const endDate = end ? dayjs(end) : null;

    if (!startDate?.isValid() || !endDate?.isValid()) {
        return null;
    }

    const diffDays = endDate.diff(startDate, 'day');

    if (diffDays < 0) {
        return changedField === 'start'
            ? 'A data inicial não pode ser maior que a data final.'
            : 'A data final não pode ser menor que a data inicial.';
    }

    if (diffDays > maxRangeDays) {
        return `O período máximo permitido é de ${maxRangeDays} dias.`;
    }

    return null;
}

const ExpandedUnavailabilityChart: FunctionComponent<{startDateTime:string, endDateTime:string, onClose: () => void}> = ({startDateTime, endDateTime, onClose}) => {
    const [rangeStartISO, setRangeStartISO] = useState<string | null>(startDateTime);
    const [rangeEndISO, setRangeEndISO] = useState<string | null>(endDateTime);
    const {data} = FindUnavailabilityPowerStationsReactQuery(rangeStartISO, rangeEndISO)
    const { enqueueSnackbar } = useSnackbar();
    const [rows, setRows] = useState<UnavailabilityRow[]>([])
    const mutate = ExportDinDexLossesReactQuery()

    const validateExportPeriod = (): {startDateTime: string; endDateTime: string} | null => {
        const validationResult = validateAndConvertDateFilters([
            {name: "startDateTime", condition: "starts-at", value: rangeStartISO || "", sort: "none"},
            {name: "endDateTime", condition: "ends-at", value: rangeEndISO || "", sort: "none"},
        ]);

        if (!rangeStartISO || !rangeEndISO || !validationResult.isValid) {
            enqueueSnackbar(buildDateFilterValidationMessage(validationResult.invalidFieldLabels), {variant: 'error'});
            return null;
        }

        return {
            startDateTime: validationResult.filters[0].value,
            endDateTime: validationResult.filters[1].value,
        };
    };

    const handleExport = (format: "csv" | "excel") => {
        const validPeriod = validateExportPeriod();
        if (!validPeriod) return;

        mutate.mutate({
            format,
            startDateTime: validPeriod.startDateTime,
            endDateTime: validPeriod.endDateTime,
        });
    };

    const handleOnColor = (data:any, columnItem: any) => {
        const colorMap: Record<string, string> = {
            din: '#2D5DE7',
            dex: '#03A9F4',
            totalpercent: '#00000099'
        };

        const name = columnItem.name.toLowerCase();

        return {
            color: colorMap[name] || '',
            backgroundColor: '',
        };
    }

    const handleRangeStartChange = (newValue: Dayjs | null) => {
        if (!newValue || !newValue.isValid()) {
            enqueueSnackbar("Informe uma data válida no campo Data inicial.", { variant: 'error' });
            return;
        }

        const error = validateDateRange(newValue, rangeEndISO, 'start');
        if (error) {
            enqueueSnackbar(error, { variant: 'warning' });
            return;
        }
        const date = newValue?.isValid() ? newValue.startOf("day").format("YYYY-MM-DDTHH:mm:ss") : null;
        setRangeStartISO(date);
    };

    const handleRangeEndChange = (newValue: Dayjs | null) => {
        if (!newValue || !newValue.isValid()) {
            enqueueSnackbar("Informe uma data válida no campo Data final.", { variant: 'error' });
            return;
        }

        const error = validateDateRange(rangeStartISO, newValue, 'end');
        if (error) {
            enqueueSnackbar(error, { variant: 'warning' });
            return;
        }
        const date = newValue?.isValid() ? newValue.endOf("day").format("YYYY-MM-DDTHH:mm:ss") : null;
        setRangeEndISO(date);
    };

    const buildRows = (info: any) => {

        const groupedMap = new Map<number, any>();
        info.forEach((item: any) => {
            if (!groupedMap.has(item.powerStationId)) {
                groupedMap.set(item.powerStationId, {
                    id: item.powerStationId,
                    position: 1,
                    station: item.powerStationName,
                    din: 0,
                    dex: 0,
                    total: 0
                });
            }
            const station = groupedMap.get(item.powerStationId);
            item.unavailabilityItem.forEach((uType: any) => {
                const formattedValue = formatNumberWithMaxTwoDecimals(uType.value);

                if (uType.name === 'DIN') {
                    station.din += formattedValue;
                } else if (uType.name === 'DEX') {
                    station.dex += formattedValue;
                }
            });
            station.total = station.din + station.dex;
        });

        const allStations = Array.from(groupedMap.values());
        const maxValue = allStations.reduce((acc, item) => acc + item.total, 0)
        return allStations.sort((a, b) => b.total - a.total).map((item, index) =>
        {
            return {
                ...item,
                position: index + 1,
                din: item.din ? formatNumberWithMaxTwoDecimals(item.din) : '0',
                dex: item.dex ? formatNumberWithMaxTwoDecimals(item.dex) : '0',
                total: item.total ? formatNumberWithMaxTwoDecimals(item.total) : '0',
                totalPerCent: maxValue === 0 ? '0%' : (item.total / maxValue * 100).toFixed(2) + '%'
            }
        }
        );
    }

    useEffect(() => {
        if(data) setRows(buildRows(data));
    }, [data]);

    return (
        <Box sx={style}>
        <ACard
            ariaLabel={"din-dex-unavailability"}
            key={`chart-power-irradiance`}
            styleProps={{
                cardStyle: { minHeight: 500, maxHeight: data && data.length > 0 ? 800 : 500},
                contentStyle: { padding: 0 },
                headerStyle: {paddingTop: 2},
                headerActionsStyle: {marginLeft: 3, marginTop: 0},
                titleStyle: {marginTop: 0, marginLeft: 2}
            }}
            headerControlsPosition={"header"}
            title={"Indisponibilidade DIN/DEX por Usina"}
            headerControls={
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
                     <Stack sx={{marginBottom: 1}} direction="row" spacing={2}>
                            <DatePicker
                                disabled={true}
                                label="Data inicial"
                                inputFormat="DD/MM/YYYY"
                                value={rangeStartISO}
                                onChange={handleRangeStartChange}
                                renderInput={(params: any) => <TextField size="small" sx={{ width: 170 }} {...params} />}
                            />
                            <DatePicker
                                disabled={true}
                                label="Data final"
                                inputFormat="DD/MM/YYYY"
                                value={rangeEndISO}
                                onChange={handleRangeEndChange}
                                renderInput={(params: any) => <TextField size="small" sx={{ width: 170 }} {...params} />}
                            />
                     </Stack>
                </LocalizationProvider>
            }
            headerActions={
                <Stack sx={{width: '100%', marginRight: 6}} direction="column" spacing={"2px"}>
                    <Box onClick={() => handleExport("csv")} sx={{ width: '100%', display: 'flex',alignItems: 'center', justifyContent: 'space-between', padding: '0 5px'}}>
                        <IconButton size="small"  sx={{borderRadius: '4px', width: '100%', justifyContent: 'flex-start'}}>
                            <SimCardDownloadOutlined sx={{marginRight: '12px'}}/>
                            <Typography variant="body2">Baixar como CSV</Typography>
                        </IconButton>
                    </Box>
                    <Divider />
                    <Box onClick={() => handleExport("excel")} sx={{ width: '100%', display: 'flex',alignItems: 'center', justifyContent: 'space-between', padding: '0 5px'}}>
                        <IconButton size="small"  sx={{borderRadius: '4px', width: '100%', justifyContent: 'flex-start'}}>
                            <SimCardDownloadOutlined sx={{marginRight: '12px'}}/>
                            <Typography variant="body2">Baixar como Excel</Typography>
                        </IconButton>
                    </Box>
                    <Divider />
                </Stack>
            }
            InformationTooltip={
                <Stack direction="row" spacing={"2px"} alignItems="center">
                    <Tooltip describeChild title="Mostra a perda energética por usina no período, de forma segmentada por DIN e DEX." arrow placement={'top-end'}>
                        <InfoOutlined fontSize='small' style={{ backgroundColor: themeDefaults.colors.primary,color: 'white', borderRadius: '50%', border: '1px solid rgba(45, 93, 231, 1)',}}/>
                    </Tooltip>
                    <IconButton
                        onClick={onClose}
                        size="small"
                        >
                        <CloseIcon />
                    </IconButton>
                </Stack>
            }

        >
            <UnavailabilityBarChart data={data} isExpansive={true} height={350}/>
            {data && data.length > 0 && (
                <>
                <Divider sx={{marginTop: 1}}/>
                <Box sx={{paddingRight: 3, paddingLeft: 3, marginBottom: 2}}>
                    <Box sx={{
                        justifyContent: 'center',
                        marginTop: 1
                    }}>
                        <Typography
                            sx={{ fontWeight: 600, color: '#333', fontSize: '0.8rem' }} // Estilização opcional para destacar
                        >
                            TABELA DE DADOS COMPLETA
                        </Typography>
                    </Box>
                    <ADataGrid
                        maxHeight={300}
                        hideSelection={true}
                        ariaLabel={'din-dex-full-table'}
                        hideFilters={true}
                        size={"small"}
                        columns={columns}
                        rows={rows}
                        onColorSelect={handleOnColor}
                        tableContainerStyle={{borderRadius: 0, marginTop: 1, marginBottom: 0}}
                        headerStyle={{ color: '#626262', textTransform: 'capitalize', backgroundColor: '#F5F5F5', margin: 0, borderBottom: '2px solid #e0e0e0', fontSize: '7px !important' }}
                        page={1}
                        loading={false}
                        totalOfRecords={1}
                        rowsPerPage={15}
                        showSortOptions={false}
                        disablePagination={true}
                    />
                </Box>
                </>
            )}
        </ACard>
        </Box>
    );
}

export default ExpandedUnavailabilityChart;
